/**
 * real-google change: Admin -> System operations -> Integrations (CHANGE-real-google section 2).
 * Google Cloud OAuth client administration: desktop client id + write-only secret, optional web
 * client for Google Workspace sign-in, hosted domain, the fixed scope list, a Test action and the
 * setup checklist. Secrets are pasted once and never returned; the configuration only carries
 * `set / not set` and the update time.
 */
import { api } from '@agency/shared';
import { Button, Callout, Card, Chip, Field, Input, KeyValue, PageHeader, Skeleton, Switch, fmtTimestamp, useToast } from '@agency/ui';
import { ExternalLink, FlaskConical, Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ErrorCallout, Grid, Page, RequireCapability, SensitiveButton, UserLink } from '../../app/widgets.js';
import { ApiError, call, shortId } from '../../lib/api.js';
import { describeError, useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { useApi } from '../../lib/useApi.js';
import {
  EMPTY_FORM,
  SETUP_CHECKLIST,
  STATUS_LABEL,
  STATUS_TONE,
  buildUpdateRequest,
  canEnableWorkforceSignIn,
  clientIdWarning,
  fieldErrors,
  formFromConfig,
  headerSubtitle,
  lastTestSummary,
  secretHint,
  testResultLine,
  type GoogleForm,
} from './integrationsState.js';

export function IntegrationsPage() {
  return (
    <RequireCapability capability="device.manage">
      <IntegrationsBody />
    </RequireCapability>
  );
}

function IntegrationsBody() {
  const session = useSession();
  const mutation = useMutation();
  const { notify } = useToast();
  const q = useApi('admin.integrations.google.get');
  const cfg = q.data;
  const canManage = session.has('device.manage');
  const [form, setForm] = useState<GoogleForm>(EMPTY_FORM);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<api.GoogleIntegrationTestResponse | null>(null);
  // Reset the form only when the editable part of the configuration changes (a Test reload that
  // touches lastTestedAt must not drop unsaved edits).
  const editableKey = cfg ? JSON.stringify([formFromConfig(cfg), cfg.desktopClientSecretSet, cfg.webClientSecretSet]) : null;
  useEffect(() => {
    if (editableKey) setForm((JSON.parse(editableKey) as [GoogleForm, boolean, boolean])[0]);
  }, [editableKey]);

  const body = useMemo(() => (cfg ? buildUpdateRequest(form, cfg) : null), [form, cfg]);
  const errors = useMemo(() => fieldErrors(body), [body]);
  const hasErrors = Object.keys(errors).length > 0;
  const workforceReady = canEnableWorkforceSignIn(form, cfg);
  const set = <K extends keyof GoogleForm>(key: K, value: GoogleForm[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    if (!cfg || !body || hasErrors) return;
    const saved = await mutation.run('admin.integrations.google.update', { input: body }, { title: 'Google integration updated', invalidate: ['admin.integrations.google'] });
    if (saved) {
      setTestResult(null);
      setForm({ ...formFromConfig(saved) });
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await call('admin.integrations.google.test', { input: {} });
      setTestResult(res.data);
      notify({
        title: res.data.ok ? 'Google test passed' : 'Google test failed',
        description: res.auditEventId ? `Recorded · evt ${shortId(res.auditEventId)}` : undefined,
        tone: res.data.ok ? 'ok' : 'warn',
      });
      void q.reload();
    } catch (e) {
      const err = ApiError.from(e);
      if (err.status !== 401) notify({ title: 'Request failed', description: describeError(err), tone: 'brand', duration: 0 });
    } finally {
      setTesting(false);
    }
  };

  const line = testResult ? testResultLine(testResult) : null;

  return (
    <Page>
      <PageHeader
        eyebrow="System operations"
        eyebrowPath="Integrations"
        title="Integrations"
        subtitle={cfg ? headerSubtitle(cfg) : 'Loading integration configuration.'}
        actions={
          <>
            <Button variant="tertiary" icon={FlaskConical} trailingArrow={false} loading={testing} disabled={!cfg || !canManage || mutation.busy} onClick={() => void test()}>
              Test
            </Button>
            <SensitiveButton variant="primary" icon={Save} disabled={!cfg || !canManage || !body || hasErrors} loading={mutation.busy} onClick={() => void save()}>
              Save
            </SensitiveButton>
          </>
        }
      />
      <ErrorCallout error={q.error} onRetry={() => void q.reload()} className="mt-6" />
      {!cfg ? (
        <Skeleton height={360} className="mt-6" />
      ) : (
        <Grid>
          <Card
            title="Google Cloud"
            subtitle="OAuth clients · secrets stored once, never returned"
            span="col-span-12 lg:col-span-7"
            rightSlot={<Chip tone={STATUS_TONE[cfg.status]}>{STATUS_LABEL[cfg.status]}</Chip>}
          >
            {!canManage ? <Callout tone="info" className="mb-4">Changes require device.manage and recent authentication.</Callout> : null}
            <div className="flex flex-col gap-4">
              <Field label="Desktop client id" required hint="DESKTOP APP" error={errors.desktopClientId} help={clientIdWarning(form.desktopClientId) ?? 'Google OAuth client of type Desktop app. The desktop fetches it at connect time and holds it in memory only.'}>
                <Input mono value={form.desktopClientId} onChange={(e) => set('desktopClientId', e.target.value)} placeholder="123456789012-abc123.apps.googleusercontent.com" autoComplete="off" spellCheck={false} disabled={!canManage} />
              </Field>
              <Field label="Desktop client secret" hint={secretHint(cfg.desktopClientSecretSet, cfg.updatedAt)} error={errors.desktopClientSecret} help="Write-only. Paste to set or replace; leave empty to keep the stored value. Google documents that a Desktop-app secret is not confidential; PKCE still applies.">
                <Input mono type="password" value={form.desktopClientSecret} onChange={(e) => set('desktopClientSecret', e.target.value)} placeholder={cfg.desktopClientSecretSet ? '••••••••' : 'GOCSPX-…'} autoComplete="new-password" spellCheck={false} disabled={!canManage} />
              </Field>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Web client id" hint="OPTIONAL" error={errors.webClientId} help={clientIdWarning(form.webClientId) ?? 'Web application client, only for Google Workspace sign-in.'}>
                  <Input mono value={form.webClientId} onChange={(e) => set('webClientId', e.target.value)} placeholder="123456789012-def456.apps.googleusercontent.com" autoComplete="off" spellCheck={false} disabled={!canManage} />
                </Field>
                <Field label="Web client secret" hint={secretHint(cfg.webClientSecretSet, cfg.updatedAt)} error={errors.webClientSecret} help="Write-only. Clearing the web client id drops its secret.">
                  <Input mono type="password" value={form.webClientSecret} onChange={(e) => set('webClientSecret', e.target.value)} placeholder={cfg.webClientSecretSet ? '••••••••' : 'GOCSPX-…'} autoComplete="new-password" spellCheck={false} disabled={!canManage} />
                </Field>
              </div>
              <Field label="Hosted domain" hint="HD CLAIM" error={errors.hostedDomain} help="Workspace domain sent as the hd hint on connect and verified on the id token when Google Workspace sign-in is on.">
                <Input mono value={form.hostedDomain} onChange={(e) => set('hostedDomain', e.target.value)} placeholder="getagency.com" autoComplete="off" spellCheck={false} disabled={!canManage} />
              </Field>
              <div className="border-t border-hair pt-4">
                <Switch
                  label="Use Google Workspace for sign-in"
                  description={workforceReady ? 'Sign-in issuer switches to accounts.google.com for the desktop and the admin console; hd and email_verified are checked on every id token.' : 'Requires the web client id, its secret and the hosted domain.'}
                  checked={form.workforceSignIn && workforceReady}
                  disabled={!canManage || !workforceReady}
                  onCheckedChange={(v) => set('workforceSignIn', v)}
                />
              </div>
              <div className="flex flex-wrap items-center gap-3 border-t border-hair pt-4">
                <span className="label-mono">Test</span>
                {line ? (
                  <span data-testid="google-test-result" className={`font-mono text-xs-plus ${line.tone === 'ok' ? 'text-ok' : 'text-brand-text'}`}>
                    {line.text}
                  </span>
                ) : (
                  <span data-testid="google-test-result" className="font-mono text-xs-plus text-mute">
                    {lastTestSummary(cfg)}
                  </span>
                )}
              </div>
            </div>
          </Card>

          <div className="col-span-12 flex flex-col gap-6 lg:col-span-5">
            <Card title="Status" subtitle="Server-held configuration">
              <KeyValue
                items={[
                  { key: 'Status', value: <Chip tone={STATUS_TONE[cfg.status]}>{STATUS_LABEL[cfg.status]}</Chip> },
                  { key: 'Desktop client', value: cfg.desktopClientId ?? '--', mono: true },
                  { key: 'Desktop secret', value: cfg.desktopClientSecretSet ? 'set' : 'not set', mono: true, tone: cfg.desktopClientSecretSet ? 'ok' : 'mute' },
                  { key: 'Web client', value: cfg.webClientId ?? '--', mono: true },
                  { key: 'Web secret', value: cfg.webClientSecretSet ? 'set' : 'not set', mono: true, tone: cfg.webClientSecretSet ? 'ok' : 'mute' },
                  { key: 'Hosted domain', value: cfg.hostedDomain ?? '--', mono: true },
                  { key: 'Workforce sign-in', value: cfg.workforceSignIn ? `Google Workspace · ${api.GOOGLE_ISSUER}` : 'Default identity provider', mono: true },
                  { key: 'Last test', value: lastTestSummary(cfg), mono: true, tone: cfg.lastTestResult === 'failed' ? 'brand' : cfg.lastTestResult === 'ok' ? 'ok' : undefined },
                  { key: 'Updated', value: cfg.updatedAt ? fmtTimestamp(cfg.updatedAt) : '--', mono: true },
                  { key: 'Updated by', value: <UserLink userId={cfg.updatedBy} /> },
                ]}
              />
            </Card>

            <Card title="Scopes" subtitle="Fixed set · requested exactly as listed">
              <ul className="flex flex-col divide-y divide-hair" aria-label="Google scopes">
                {api.GOOGLE_INTEGRATION_SCOPES.map((scope) => (
                  <li key={scope} className="flex items-baseline justify-between gap-4 py-2 first:pt-0 last:pb-0">
                    <code className="min-w-0 truncate font-mono text-xs-plus text-ink" title={scope}>
                      {scope.replace('https://www.googleapis.com/auth/', '')}
                    </code>
                    <span className="shrink-0 text-xs text-mute">{api.GOOGLE_INTEGRATION_SCOPE_PURPOSE[scope]}</span>
                  </li>
                ))}
              </ul>
              {cfg.scopes.length !== api.GOOGLE_INTEGRATION_SCOPES.length || cfg.scopes.some((s, i) => s !== api.GOOGLE_INTEGRATION_SCOPES[i]) ? (
                <Callout tone="warn" className="mt-4">The server reports a different scope set: {cfg.scopes.join(' ')}</Callout>
              ) : null}
            </Card>

            <Card title="Setup checklist" subtitle="README · Production Google setup" rightSlot={<a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-mute hover:text-ink"><ExternalLink size={11} aria-hidden />Google Cloud console</a>}>
              <ol className="flex flex-col gap-2 text-[13px] leading-snug text-ink/85">
                {SETUP_CHECKLIST.map((item, i) => (
                  <li key={item.step} className="flex items-baseline gap-3">
                    <span className="label-mono w-4 shrink-0 tabular-nums">{i + 1}</span>
                    {item.href ? (
                      <a href={item.href} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {item.step}
                      </a>
                    ) : (
                      <span>{item.step}</span>
                    )}
                  </li>
                ))}
              </ol>
              <p className="mt-3 text-xs text-mute">Internal consent screens need no Google verification, including the restricted gmail.compose scope. The Workspace admin must allow internal apps under API controls.</p>
            </Card>
          </div>
        </Grid>
      )}
    </Page>
  );
}
